import * as React from 'react';
import {useEffect, useState} from 'react';
import Slider from 'react-slick';
import {FormattedMessage} from 'react-intl';

import {Margin, SITE, HeadlineLevel} from 'features/ui/enums';
import {getCurrentSite} from 'features/services/utils/env-helper';
import {getLocatizedDate} from 'features/services/utils/date-helpers';

import './style.scss';
import {Direction, NavArrow} from '@atoms/LinksAndButtons/NavArrow';
import {TimelineItem} from './_timelineItem';
import {Timeline, TimelineStep} from 'features/template/viewModels/ResidenceStage';
import {Headline} from '@atoms/Text/Headline';

interface TimelineItemStatus {
    isVisible: boolean;
    status: number;
}

interface TimelineItemsStatus {
    planned: TimelineItemStatus;
    vipSale: TimelineItemStatus;
    vipSaleProcessing: TimelineItemStatus;
    preSellingStart: TimelineItemStatus;
    salesStart: TimelineItemStatus;
    buildStart: TimelineItemStatus;
    interiorOptions: TimelineItemStatus;
    intermediation: TimelineItemStatus;
    finalizing: TimelineItemStatus;
    moveIn: TimelineItemStatus;
    completed: TimelineItemStatus;
}

interface Props {
    timeline: Timeline;
    isRental?: boolean;
}

export const ProjectTimeline: React.FunctionComponent<Props> = ({timeline, isRental}) => {
    let firstClientX: number = 0;
    let clientX: number = 0;
    let noOfItems: number = 0;
    let timelineItems: TimelineItemsStatus;
    const _sliderRef = React.createRef<HTMLDivElement>();
    const [hasSlided, setHasSlided] = useState<boolean>(false);

    const settings = {
        dots: true,
        infinite: false,
        speed: 500,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '33%',
        slidesToShow: 1,
        arrows: true,
        initialSlide: timeline.currentTimeline,
        nextArrow: <NavArrow direction={Direction.NEXT} />,
        prevArrow: <NavArrow direction={Direction.PREV} />,
        responsive: [
          {
            breakpoint: 1000,
            settings: {
                centerPadding: '60px',
            }
          }
        ]
    };

    useEffect(() => {
        if(_sliderRef.current) {
            _sliderRef.current.addEventListener('touchstart', touchStart);
            _sliderRef.current.addEventListener('touchmove', preventTouch, {passive: false});
        }

        //@ts-expect-error
        const target = _sliderRef.current.querySelector('.slick-track');

        const _observer = new MutationObserver((mutations) => {
            mutations.forEach((_mutationRecord) => {
                //@ts-expect-error
                if(target.style.webkitTransform !== "translate3d(0px, 0px, 0px)" && !hasSlided ) {
                    setHasSlided(true);
                }
            });
        });

        return () => {
            if(_sliderRef.current) {
                _sliderRef.current.removeEventListener('touchstart', touchStart);
                //@ts-expect-error
                _sliderRef.current.removeEventListener('touchmove', preventTouch, {passive: false});
            }
        }

    }, [_sliderRef.current]);

    const expireTimeLineBefore = (timeline: TimelineItemsStatus, item: TimelineStep) => {
        if (item !== TimelineStep.PLANNED) timeline.planned.status = -1; else { timeline.planned.status = 1; return timeline; }
        if (item !== TimelineStep.VIP_SALE) timeline.vipSale.status = -1; else { timeline.vipSale.status = 1; return timeline; }
        if (item !== TimelineStep.VIP_SALE_PROCESSING) timeline.vipSaleProcessing.status = -1; else { timeline.vipSaleProcessing.status = 1; return timeline; }
        if (item !== TimelineStep.PRE_SELLING) timeline.preSellingStart.status = -1; else { timeline.preSellingStart.status = 1; return timeline; }
        if (item !== TimelineStep.SALES_START) timeline.salesStart.status = -1; else { timeline.salesStart.status = 1; return timeline; }
        if (item !== TimelineStep.BUILD_START) timeline.buildStart.status = -1; else { timeline.buildStart.status = 1; return timeline; }
        if (item !== TimelineStep.INTERIOR_OPTIONS) timeline.interiorOptions.status = -1; else { timeline.interiorOptions.status = 1; return timeline; }
        if (item !== TimelineStep.INTERMEDIATION) timeline.intermediation.status = -1; else { timeline.intermediation.status = 1; return timeline; }
        if (item !== TimelineStep.FINALIZING) timeline.finalizing.status = -1; else { timeline.finalizing.status = 1; return timeline; }
        if (item !== TimelineStep.MOVE_IN) timeline.moveIn.status = -1; else { timeline.moveIn.status = 1; return timeline; }

        return timeline;
    };

    const getTimelineSteps = (): TimelineStep[] => {
        const site = getCurrentSite();

        const defaultSteps = [TimelineStep.PLANNED, TimelineStep.VIP_SALE, TimelineStep.VIP_SALE_PROCESSING, TimelineStep.SALES_START, TimelineStep.BUILD_START, TimelineStep.INTERIOR_OPTIONS, TimelineStep.FINALIZING, TimelineStep.MOVE_IN ];
        const jmnoSteps = [TimelineStep.PLANNED, TimelineStep.VIP_SALE, TimelineStep.VIP_SALE_PROCESSING, TimelineStep.SALES_START, TimelineStep.BUILD_START, TimelineStep.INTERIOR_OPTIONS, TimelineStep.FINALIZING ,TimelineStep.MOVE_IN ];
        const jmoySteps = [TimelineStep.PLANNED, TimelineStep.VIP_SALE, TimelineStep.VIP_SALE_PROCESSING, TimelineStep.PRE_SELLING, TimelineStep.SALES_START, TimelineStep.BUILD_START, TimelineStep.INTERIOR_OPTIONS, TimelineStep.MOVE_IN ];
        const rentalSteps = [TimelineStep.PLANNED, TimelineStep.BUILD_START, TimelineStep.INTERMEDIATION, TimelineStep.MOVE_IN, TimelineStep.COMPLETED ];

        if (site === SITE.FI) return jmoySteps;
        if (site === SITE.NO) return jmnoSteps;
        if (isRental) return rentalSteps;

        return defaultSteps;
    };

    const getStepVisiblity = (currentStep:TimelineStep, steps:TimelineStep[], hideStep?: boolean): boolean => {
        if (steps.indexOf(currentStep) > -1 && (typeof hideStep === 'undefined' || hideStep === false)) noOfItems += 1;
        if (steps.indexOf(currentStep) > -1 && (typeof hideStep === 'undefined' || hideStep === false)) return true;
        return false;
    };

    const getTimelineItemsStatus = (): TimelineItemsStatus => {
        const steps = getTimelineSteps();
        const now = new Date();

        const timelineItems = {
            planned : { isVisible: getStepVisiblity(TimelineStep.PLANNED, steps), status: -1},
            vipSale : { isVisible: getStepVisiblity(TimelineStep.VIP_SALE, steps, timeline.vipStartHide), status: timeline.vipStart != null && new Date(timeline.vipStart) < now ? -1 : 0},
            vipSaleProcessing : { isVisible: getStepVisiblity(TimelineStep.VIP_SALE_PROCESSING, steps, timeline.vipProcessingHide), status: timeline.vipProcessingStart != null && new Date(timeline.vipProcessingStart) < now ? -1 : 0},
            preSellingStart : { isVisible: getStepVisiblity(TimelineStep.PRE_SELLING, steps, timeline.preSellingHide), status: timeline.preSellingStart != null && new Date(timeline.preSellingStart) < now ? -1 : 0},
            salesStart : { isVisible: getStepVisiblity(TimelineStep.SALES_START, steps), status: timeline.saleStart != null && new Date(timeline.saleStart) < now ? -1 : 0},
            buildStart : { isVisible: getStepVisiblity(TimelineStep.BUILD_START, steps, timeline.buildHide), status: timeline.buildStart != null && new Date(timeline.buildStart) < now ? -1 : 0},
            interiorOptions : { isVisible: getStepVisiblity(TimelineStep.INTERIOR_OPTIONS, steps, timeline.interiorHide), status: timeline.interiorStart != null && new Date(timeline.interiorStart) < now ? -1 : 0},
            intermediation: {isVisible : getStepVisiblity(TimelineStep.INTERMEDIATION, steps), status: timeline.intermediationStart != null && new Date(timeline.intermediationStart) < now ? -1 : 0},
            finalizing: {isVisible: getStepVisiblity(TimelineStep.FINALIZING, steps), status: timeline.finalizingStart != null && new Date(timeline.finalizingStart) < now ? -1 : 0},
            moveIn : { isVisible: getStepVisiblity(TimelineStep.MOVE_IN, steps), status: timeline.moveInStart != null && new Date(timeline.moveInStart) < now ? -1 : 0},
            completed : {isVisible : getStepVisiblity(TimelineStep.COMPLETED, steps), status: timeline.completedDate != null && new Date(timeline.completedDate) < now ? -1 : 0}
        };

        if (timelineItems.completed.isVisible && timelineItems.completed.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.COMPLETED);
        }
        if (timelineItems.moveIn.isVisible && timelineItems.moveIn.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.MOVE_IN);
        }
        if (timelineItems.finalizing.isVisible && timelineItems.finalizing.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.FINALIZING);
        }
        if (timelineItems.intermediation.isVisible && timelineItems.intermediation.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.INTERMEDIATION);
        }
        if (timelineItems.interiorOptions.isVisible && timelineItems.interiorOptions.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.INTERIOR_OPTIONS);
        }
        if (timelineItems.buildStart.isVisible && timelineItems.buildStart.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.BUILD_START);
        }
        if (timelineItems.salesStart.isVisible && timelineItems.salesStart.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.SALES_START);
        }
        if (timelineItems.preSellingStart.isVisible && timelineItems.preSellingStart.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.PRE_SELLING);
        }
        if (timelineItems.vipSaleProcessing.isVisible && timelineItems.vipSaleProcessing.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.VIP_SALE_PROCESSING);
        }
        if (timelineItems.vipSale.isVisible && timelineItems.vipSale.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.VIP_SALE);
        }
        if (timelineItems.planned.isVisible && timelineItems.planned.status === -1){
            return expireTimeLineBefore(timelineItems, TimelineStep.PLANNED);
        }

        return timelineItems;
    };

    const touchStart = (e: TouchEvent) => {
        firstClientX = e.touches[0].clientX;
    };

    const preventTouch = (e: TouchEvent) =>{
        const minValue = 5; // threshold

        clientX = e.touches[0].clientX - firstClientX;

        // Vertical scrolling does not work when you start swiping horizontally.
        if(Math.abs(clientX) > minValue){
            e.preventDefault();
            e.returnValue = false;
            return false;
        }

        return;
    };

    timelineItems = getTimelineItemsStatus();
    const isRentalVar = isRental ? ".rental" : "";

    return (
        <div className="project-calendar" ref={_sliderRef}>
            <Headline level={HeadlineLevel.H2} center margin={Margin.L}><FormattedMessage id="jm.components.ProjectTimeline.header" /></Headline>
            <Slider {...settings} >

                {timelineItems.planned.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.planned}
                        headlineId="jm.components.ProjectTimeline.planned"
                        descriptionId={`jm.components.ProjectTimeline.plannedDescription${isRentalVar}`}>
                            <></>
                    </TimelineItem>
                }

                {timelineItems.vipSale.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.vipSale}
                        headlineId="jm.components.ProjectTimeline.vipSale"
                        descriptionId="jm.components.ProjectTimeline.vipSaleDescription">
                        {(timeline.vipStart || timeline.vipStartPreliminaryText) &&
                            <span>
                                {timeline.vipStartPreliminaryText ?
                                    `${timeline.vipStartPreliminaryText}` :
                                    timeline.vipStart ?
                                        timeline.vipEnd ?
                                            `${getLocatizedDate(timeline.vipStart)} - ${getLocatizedDate(timeline.vipEnd)}` :
                                            `${getLocatizedDate(timeline.vipStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.vipSaleProcessing.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.vipSaleProcessing}
                        headlineId="jm.components.ProjectTimeline.vipSaleProcessing"
                        descriptionId="jm.components.ProjectTimeline.vipSaleProcessingDescription">
                        {(timeline.vipProcessingStart || timeline.vipProcessingPreliminaryText) &&
                            <span>
                                {timeline.vipProcessingPreliminaryText ?
                                    `${timeline.vipProcessingPreliminaryText}` :
                                    timeline.vipProcessingStart ?
                                        timeline.vipProcessingEnd ?
                                            `${getLocatizedDate(timeline.vipProcessingStart)} - ${getLocatizedDate(timeline.vipProcessingEnd)}` :
                                            `${getLocatizedDate(timeline.vipProcessingStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.preSellingStart.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.preSellingStart}
                        headlineId="jm.components.ProjectTimeline.preSellingStart"
                        descriptionId="jm.components.ProjectTimeline.preSellingStartDescription">
                        {(timeline.preSellingStart || timeline.preSellingPreliminaryText) &&
                            <span>
                                {timeline.preSellingPreliminaryText ?
                                    `${timeline.preSellingPreliminaryText}` :
                                    timeline.preSellingStart ?
                                        timeline.preSellingEnd ?
                                            `${getLocatizedDate(timeline.preSellingStart)} - ${getLocatizedDate(timeline.preSellingEnd)}` :
                                            `${getLocatizedDate(timeline.preSellingStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.salesStart.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.salesStart}
                        headlineId="jm.components.ProjectTimeline.salesStart"
                        descriptionId="jm.components.ProjectTimeline.salesStartDescription">
                        {(timeline.saleStart || timeline.salePreliminaryText) &&
                            <span>
                                {timeline.salePreliminaryText ?
                                    `${timeline.salePreliminaryText}` :
                                    timeline.saleStart ?
                                        timeline.saleEnd ?
                                            `${getLocatizedDate(timeline.saleStart)} - ${getLocatizedDate(timeline.saleEnd)}` :
                                            `${getLocatizedDate(timeline.saleStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.buildStart.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.buildStart}
                        headlineId="jm.components.ProjectTimeline.buildStart"
                        descriptionId={`jm.components.ProjectTimeline.buildStartDescription${isRentalVar}`}>
                        {(timeline.buildStart || timeline.buildPreliminaryText) &&
                            <span>
                                {timeline.buildPreliminaryText ?
                                    `${timeline.buildPreliminaryText}` :
                                    timeline.buildStart ?
                                        timeline.buildEnd ?
                                            `${getLocatizedDate(timeline.buildStart)} - ${getLocatizedDate(timeline.buildEnd)}` :
                                            `${getLocatizedDate(timeline.buildStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.interiorOptions.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.interiorOptions}
                        headlineId="jm.components.ProjectTimeline.interiorOptions"
                        descriptionId="jm.components.ProjectTimeline.interiorOptionsDescription">
                        {(timeline.interiorStart || timeline.interiorPreliminaryText) &&
                            <span>
                                {timeline.interiorPreliminaryText ?
                                    `${timeline.interiorPreliminaryText}` :
                                    timeline.interiorStart ?
                                        timeline.interiorEnd ?
                                            `${getLocatizedDate(timeline.interiorStart)} - ${getLocatizedDate(timeline.interiorEnd)}` :
                                            `${getLocatizedDate(timeline.interiorStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.intermediation.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.intermediation}
                        headlineId="jm.components.ProjectTimeline.intermediation"
                        descriptionId="jm.components.ProjectTimeline.intermediationDescription">
                        {(timeline.intermediationStart || timeline.intermediationPreliminaryText) &&
                            <span>
                                {timeline.intermediationPreliminaryText ?
                                    `${timeline.intermediationPreliminaryText}` :
                                    timeline.intermediationStart ?
                                        timeline.intermediationEnd ?
                                            `${getLocatizedDate(timeline.intermediationStart)} - ${getLocatizedDate(timeline.intermediationEnd)}` :
                                            `${getLocatizedDate(timeline.intermediationStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.finalizing.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.finalizing}
                        headlineId="jm.components.ProjectTimeline.finalizing"
                        descriptionId={"jm.components.ProjectTimeline.finalizingDescription"}>
                        {(timeline.finalizingStart || timeline.finalizingPreliminaryText) &&
                            <span>
                                {timeline.finalizingPreliminaryText ?
                                    `${timeline.finalizingPreliminaryText}` :
                                    timeline.finalizingStart ?
                                        timeline.finalizingEnd ?
                                            `${getLocatizedDate(timeline.finalizingStart)} - ${getLocatizedDate(timeline.finalizingEnd)}` :
                                            `${getLocatizedDate(timeline.finalizingStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.moveIn.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.moveIn}
                        headlineId="jm.components.ProjectTimeline.inhabit"
                        descriptionId={`jm.components.ProjectTimeline.inhabitDescription${isRentalVar}`}>
                        {(timeline.moveInStart || timeline.moveInPreliminaryText) &&
                            <span>
                                {timeline.moveInPreliminaryText ?
                                    `${timeline.moveInPreliminaryText}` :
                                    timeline.moveInStart ?
                                        timeline.moveInEnd ?
                                            `${getLocatizedDate(timeline.moveInStart)} - ${getLocatizedDate(timeline.moveInEnd)}` :
                                            `${getLocatizedDate(timeline.moveInStart)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }

                {timelineItems.completed.isVisible &&
                    <TimelineItem
                        timelineItem={timelineItems.completed}
                        headlineId="jm.components.ProjectTimeline.completed"
                        descriptionId="jm.components.ProjectTimeline.completedDescription">
                        {(timeline.completedDate || timeline.completedPreliminaryText) &&
                            <span>
                                {timeline.completedPreliminaryText ?
                                    `${timeline.completedPreliminaryText}` :
                                    timeline.completedDate ?
                                        `${getLocatizedDate(timeline.completedDate)}` :
                                        ``
                                }
                            </span>
                        }
                    </TimelineItem>
                }
            </Slider>
            <div className='slick-counter'>
                <span>/ {noOfItems}</span>
            </div>
        </div>
    )
};
